import React from 'react';
import {IntlShape} from 'react-intl';
import {TooltipFormulaItem, TooltipFormulaRenderer} from '../TooltipFormulaRenderer';

type FormatMessage = IntlShape['formatMessage'];

const getCostToolTip = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'baseline.baseline_cost'}),
		description: formatMessage({id: 'baseline.cost_header_tooltip_description'}),
		details: [
			formatMessage({id: 'common.work_estimate'}),
			'x',
			formatMessage({id: 'common.cost_per_hour'}),
			'+',
			formatMessage({id: 'baseline.cost_expenses'}),
		],
	},
];

const getFixedPriceRevenueToolTip = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'baseline.baseline_revenue'}),
		description: formatMessage({id: 'baseline.total_baseline_revenue_tooltip_fixed_price'}),
	},
];

const getRevenueToolTip = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'baseline.baseline_revenue'}),
		description: formatMessage({id: 'baseline.revenue_description'}),
		details: [
			formatMessage({id: 'baseline.work_estimate'}),
			'x',
			formatMessage({id: 'baseline.rate_per_hour'}),
			'+',
			formatMessage({id: 'project_budget.revenue_from_expenses'}),
		],
	},
];

const getProfitToolTip = (
	formatMessage: FormatMessage,
	isFixedPrice: boolean,
	useFixedPriceCalculation: boolean | null
): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'baseline.baseline_profit'}),
		details: [
			formatMessage({
				id: isFixedPrice && !useFixedPriceCalculation ? 'baseline.value_of_service' : 'project_budget.baseline_revenue',
			}),
			'-',
			formatMessage({id: 'baseline.baseline_cost'}),
		],
	},
];

const getValueOfServiceToolTip = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'baseline.value_of_service'}),
		description: formatMessage({id: 'baseline.total_baseline_value_of_service_header_tooltip_description'}),
	},
];

const getMarginToolTip = (
	formatMessage: FormatMessage,
	isFixedPrice: boolean,
	useFixedPriceCalculation: boolean | null
): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'baseline.baseline_margin'}),
		details: [
			formatMessage({id: 'baseline.baseline_profit'}),
			'/',
			formatMessage({
				id: isFixedPrice && !useFixedPriceCalculation ? 'baseline.value_of_service' : 'project_budget.baseline_revenue',
			}),
			'x',
			'100',
		],
	},
];

type HeaderTooltipColumn = 'cost' | 'revenue' | 'profit' | 'margin' | 'value_of_service';

export const getTooltipForHeader = (
	column: HeaderTooltipColumn,
	formatMessage: FormatMessage,
	isFixedPrice: boolean,
	useFixedPriceCalculation: boolean | null
): JSX.Element | null => {
	switch (column) {
		case 'cost':
			return <TooltipFormulaRenderer items={getCostToolTip(formatMessage)} />;
		case 'revenue':
			if (isFixedPrice) {
				return <TooltipFormulaRenderer items={getFixedPriceRevenueToolTip(formatMessage)} />;
			} else {
				return <TooltipFormulaRenderer items={getRevenueToolTip(formatMessage)} />;
			}
		case 'profit':
			return <TooltipFormulaRenderer items={getProfitToolTip(formatMessage, isFixedPrice, useFixedPriceCalculation)} />;
		case 'margin':
			return <TooltipFormulaRenderer items={getMarginToolTip(formatMessage, isFixedPrice, useFixedPriceCalculation)} />;
		case 'value_of_service':
			return <TooltipFormulaRenderer items={getValueOfServiceToolTip(formatMessage)} />;
		default:
			return null;
	}
};

type GroupedFinancialNumbersRow = {
	readonly baselineRevenue: number | null;
	readonly baselineTimeAndExpenses: number | null;
	readonly baselineCost: number | null;
	readonly baselineProfit: number | null;
	readonly phaseBaselineRoleId: number | null;
	readonly phaseBaselineExpenseId: number | null;
	readonly phaseId: number | null;
};

type FinancialTotals = {
	totalBaselineRevenue: number;
	totalBaselineTimeAndExpenses: number;
	totalCost: number;
	totalProfit: number;
	totalMargin: number;
};

type PhaseFinancials<Row extends GroupedFinancialNumbersRow> = FinancialTotals & {
	phaseId: number | null;
	roles: Map<number, Row>;
	expenses: Map<number, Row>;
};

export const getGroupedFinancialNumbersByPhaseAndBaselineId = <Row extends GroupedFinancialNumbersRow>(
	groupedFinancialNumbers: ReadonlyArray<Row | null>
): {totalFinancialNumbers: FinancialTotals; financialNumbersMap: Map<number | null, PhaseFinancials<Row>>} => {
	const financialNumbersMap = new Map<number | null, PhaseFinancials<Row>>();
	const totalFinancialNumbers: FinancialTotals = {
		totalBaselineRevenue: 0,
		totalBaselineTimeAndExpenses: 0,
		totalCost: 0,
		totalProfit: 0,
		totalMargin: 0,
	};
	groupedFinancialNumbers.forEach(financialNumbers => {
		if (!financialNumbers) return;
		const {phaseId, phaseBaselineRoleId, phaseBaselineExpenseId} = financialNumbers;
		// A null amount adds nothing, exactly as `+= null` did before these were typed.
		const baselineRevenue = financialNumbers.baselineRevenue ?? 0;
		const baselineTimeAndExpenses = financialNumbers.baselineTimeAndExpenses ?? 0;
		const baselineCost = financialNumbers.baselineCost ?? 0;
		const baselineProfit = financialNumbers.baselineProfit ?? 0;
		const phaseFinancials: PhaseFinancials<Row> = financialNumbersMap.get(phaseId) || {
			phaseId,
			totalBaselineRevenue: 0,
			totalBaselineTimeAndExpenses: 0,
			totalCost: 0,
			totalProfit: 0,
			totalMargin: 0.0,
			roles: new Map(),
			expenses: new Map(),
		};
		phaseFinancials.totalBaselineRevenue += baselineRevenue;
		totalFinancialNumbers.totalBaselineRevenue += baselineRevenue;
		phaseFinancials.totalBaselineTimeAndExpenses += baselineTimeAndExpenses;
		totalFinancialNumbers.totalBaselineTimeAndExpenses += baselineTimeAndExpenses;
		phaseFinancials.totalCost += baselineCost;
		totalFinancialNumbers.totalCost += baselineCost;
		phaseFinancials.totalProfit += baselineProfit;
		totalFinancialNumbers.totalProfit += baselineProfit;
		if (phaseBaselineRoleId) {
			phaseFinancials.roles.set(phaseBaselineRoleId, financialNumbers);
		} else if (phaseBaselineExpenseId) {
			phaseFinancials.expenses.set(phaseBaselineExpenseId, financialNumbers);
		}
		financialNumbersMap.set(phaseId, phaseFinancials);
	});

	financialNumbersMap.forEach(phaseFinancials => {
		const {totalBaselineRevenue, totalProfit} = phaseFinancials;
		phaseFinancials.totalMargin = totalProfit / totalBaselineRevenue;
	});
	totalFinancialNumbers.totalMargin = totalFinancialNumbers.totalProfit / totalFinancialNumbers.totalBaselineRevenue;

	return {totalFinancialNumbers, financialNumbersMap};
};
